'use client';
import { ComponentType, forwardRef, ReactNode } from 'react';
import { format as formatUrl } from 'url';
import {
  Box,
  buttonClasses,
  // eslint-disable-next-line no-restricted-imports
  ButtonProps as MuiButtonProps,
  SxProps,
  Theme,
} from '@mui/material';

// eslint-disable-next-line no-restricted-imports
import NextLink from 'next/link';

import mixinSx from '@watershed/style/mixinSx';

import Tooltip, { type TooltipProps } from '../components/Tooltip';
import usePaletteUtils from '../hooks/usePaletteUtils';
import type { WatershedLinkProps } from '../types/LinkProps';
import isExternalUrl, { isApiRouteUrl } from '../utils/isExternalUrl';

interface CommonButtonProps
  extends Pick<
    MuiButtonProps,
    'sx' | 'ref' | 'disabled' | 'children' | 'fullWidth'
  > {}

export type WatershedButtonProps<Props extends CommonButtonProps> = Omit<
  Props,
  'to' | 'href'
> &
  Omit<WatershedLinkProps, 'onClick' | 'children'> & {
    id?: string;
    tooltip?: ReactNode;
    tooltipProps?: Partial<TooltipProps>;
    tooltipPlacement?: TooltipProps['placement'];
    isIcon?: boolean;
    tooltipDisableInteractive?: boolean;
    flat?: boolean;
    buttonWrapperSx?: SxProps<Theme>;
  };

export default function createWatershedButton<Props extends CommonButtonProps>(
  ButtonToWrap: ComponentType<CommonButtonProps>
) {
  type ButtonProps = WatershedButtonProps<Props>;
  return forwardRef<HTMLButtonElement, ButtonProps>(
    function Button(propsWithRef, ref) {
      const props = propsWithRef as ButtonProps;
      const {
        href,
        children,
        replace,
        as: _as,
        scroll,
        prefetch,
        newTab,
        tooltip,
        tooltipProps,
        tooltipPlacement,
        buttonWrapperSx,
        isIcon,
        tooltipDisableInteractive,
        flat,
        sx = [],
        ...buttonProps
      } = props;

      const paletteUtils = usePaletteUtils();
      const hrefString = href ? formatUrl(href) : '';
      const isExternal = href !== undefined && isExternalUrl(hrefString);
      const isApiRoute = href !== undefined && isApiRouteUrl(hrefString);

      const component = (() => {
        if (href && !newTab && !isExternal && !isApiRoute) {
          return (
            <ButtonToWrap
              {...buttonProps}
              component={NextLink}
              href={href}
              replace={replace}
              scroll={scroll}
              prefetch={prefetch}
              ref={ref}
              sx={sx}
            >
              {children}
            </ButtonToWrap>
          );
        }

        return (
          <ButtonToWrap
            href={href ? hrefString : undefined}
            {...buttonProps}
            {...(newTab ? { target: '_blank' } : {})}
            {...(isExternal
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {})}
            component={href ? 'a' : 'button'}
            ref={ref}
            sx={mixinSx(
              isIcon ? { minWidth: 32, padding: 0 } : undefined,
              flat && {
                // Specificity necessary to override contained & secondary styling on base button
                [`&&.${buttonClasses.root}`]: {
                  boxShadow: paletteUtils.boxShadowField.base,
                  '&:hover': { boxShadow: paletteUtils.boxShadowField.base },
                  '&:focus': { boxShadow: paletteUtils.boxShadowField.base },
                },
              },
              ...(Array.isArray(sx) ? sx : [sx])
            )}
          >
            {children}
          </ButtonToWrap>
        );
      })();

      if (tooltip || buttonWrapperSx) {
        return (
          <Tooltip
            title={tooltip}
            placement={tooltipPlacement}
            disableInteractive={tooltipDisableInteractive}
            {...tooltipProps}
          >
            <Box
              tabIndex={buttonProps.disabled ? 0 : undefined}
              width={buttonProps.fullWidth ? '100%' : 'initial'}
              sx={mixinSx(
                {
                  borderRadius: 1,
                  '&:focus, &:focus-visible': {
                    outline: 'none',
                  },
                  '&:focus-visible': {
                    boxShadow: paletteUtils.boxShadowButton.focus,
                  },
                },
                buttonWrapperSx
              )}
            >
              {component}
            </Box>
          </Tooltip>
        );
      }

      return component;
    }
  );
}
